import { UserCard } from "@/components/cards";
import { useProtocol } from "@/hooks/use-protocol";
import { useProtocolCallRequestEvents } from "@/hooks/use-protocol-call-request-events";
import { LogCallRequestEventModal } from "@/modals/protocols";
import { Button, ButtonGroup } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Panels } from "@msuite/ui/panels";
import { DataTable } from "@msuite/ui/table";
import { Tag } from "@msuite/ui/tag";
import { guardArray } from "@msuite/utils/array";
import { moment } from "@msuite/utils/date";
import React from "react";
import type { ProtocolPanelBaseProps } from "../shared";
import { columns } from "./table-config";

export function ProtocolCallRequest({ protocolId }: ProtocolPanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();
	const resolveModal = useDisclosure();
	const attemptModal = useDisclosure();

	/** Query */
	const { protocol } = useProtocol(protocolId);
	const { callRequestEvents, status, isLoading, queryKey, refetch } =
		useProtocolCallRequestEvents(protocolId);

	/** Computed */
	const events = guardArray(callRequestEvents);

	/** Render */
	return (
		<React.Fragment>
			<Panels>
				<Panels.Panel
					flex={1}
					className="bottom-space"
				>
					<ContentCard
						icon={icon("Headset")}
						title={t("protocols.call_request")}
					>
						<DataGrid>
							<DataGrid.Row>
								<DataGrid.Label>{t("general.state")}</DataGrid.Label>
								<DataGrid.Cell>
									<Tag
										variant="plain"
										color={status.isResolved ? "emerald" : "amber"}
										size="md"
									>
										{status.isResolved
											? t("protocols.callback_resolved")
											: t("protocols.callback_open")}
									</Tag>
								</DataGrid.Cell>
							</DataGrid.Row>
							<DataGrid.Row>
								<DataGrid.Label>{t("protocols.callback_time")}</DataGrid.Label>
								<DataGrid.Value>
									{protocol?.callRequestCallbackTime
										? moment(protocol.callRequestCallbackTime).longFormat()
										: "--"}
								</DataGrid.Value>
							</DataGrid.Row>
							<DataGrid.Row>
								<DataGrid.Label>{t("protocols.callback_attempt")}</DataGrid.Label>
								<DataGrid.Value>
									{t("protocols.count_callback_attempts", {
										count: `${status.attemptCount}`,
									})}
								</DataGrid.Value>
							</DataGrid.Row>
							{status.resolvedEvent && (
								<React.Fragment>
									<DataGrid.Divider />
									<DataGrid.Row>
										<DataGrid.Label>{t("protocols.callback_resolved")}</DataGrid.Label>
										<DataGrid.Cell>
											<UserCard
												fontSize="md"
												withAvatar={false}
												userId={status.resolvedEvent.createdBy}
												variant="plain"
											/>
										</DataGrid.Cell>
									</DataGrid.Row>
									<DataGrid.Divider />
								</React.Fragment>
							)}
						</DataGrid>
						<ButtonGroup isAttached={false}>
							<Button
								variant="primary"
								icon={<Icon name="PhoneOutgoing" />}
								onClick={resolveModal.onOpen}
								isDisabled={status.isResolved}
								data-cy="protocol-mark-called-back-button"
							>
								{t("protocols.mark_as_called_back")}
							</Button>
							<Button
								icon={<Icon name="PhoneMissed" />}
								onClick={attemptModal.onOpen}
								data-cy="protocol-log-attempt-button"
							>
								{t("protocols.log_failed_attempt")}
							</Button>
						</ButtonGroup>
					</ContentCard>
				</Panels.Panel>
				<Panels.Panel
					flex={2}
					className="bottom-space"
				>
					<DataTable
						data={events}
						isLoading={isLoading}
						menu={null}
						queryKey={queryKey}
						columns={columns}
						refetch={refetch}
						persistenceKey="protocol-call-request-events-table"
					/>
				</Panels.Panel>
			</Panels>
			{resolveModal.isOpen && (
				<LogCallRequestEventModal
					protocolId={protocolId}
					type="resolved"
					isOpen={resolveModal.isOpen}
					onClose={resolveModal.onClose}
				/>
			)}
			{attemptModal.isOpen && (
				<LogCallRequestEventModal
					protocolId={protocolId}
					type="unreachable"
					isOpen={attemptModal.isOpen}
					onClose={attemptModal.onClose}
				/>
			)}
		</React.Fragment>
	);
}
